/**
 * The swap test, made real for a paid hook.
 *
 * The old swap test passed any piece that named the brand or carried a proof
 * quote, which is the opposite of the test: naming the brand is exactly what
 * the swap replaces. ITC Tax run 4eda4aae shipped two hooks, both reported
 * "Anchored to ITC Tax, a quoted proof":
 *
 *   "ITC Tax delivers Property Tax Optimization reviews without contacting
 *    assessors without client authorization."          (an FAQ reassurance)
 *   "ITC Tax conducts Property Tax Optimization led by former county
 *    appraisers, CPAs, CMIs, and valuation specialists."  (credentials)
 *
 * Put "KPMG" where "ITC Tax" stands and both are still true and still read as
 * specific. The rule here: a hook must carry at least one specific that the
 * cited evidence states and that is not the brand, an offering, a product, a
 * competitor, a credential or a bare place — a money/percent figure, or a
 * named mechanism, acronym or place of two words.
 *
 * Pure and deterministic. The model check (`survivesSwapTest`) stays, as a
 * second opinion.
 */

import { FIGURE_SOURCE, classifyEvidenceLine, resultFigures } from './result-evidence';

export interface HookSpecificityInput {
  hook: string;
  /** The evidence lines the piece cites (angle evidence, proof quote). */
  evidence: readonly string[];
  brandName: string | null;
  productNames: readonly string[];
  offerNames: readonly string[];
  competitorNames: readonly string[];
}

export interface HookSpecificity {
  /** Figures in the hook that the cited evidence states. */
  figures: string[];
  /** Named mechanisms, acronyms and places the evidence states. */
  named: string[];
  /** A run of the source's own words, taken from evidence that is not support. */
  sourceWords: string | null;
  /** True when the hook carries at least one specific. */
  specific: boolean;
}

/** Roles and credentials: what a competitor claims just as readily. */
const CREDENTIAL_TERMS = new Set([
  'cpa',
  'cmi',
  'cfa',
  'cfe',
  'mai',
  'asa',
  'jd',
  'cfo',
  'ceo',
  'coo',
  'cto',
  'vp',
  'llc',
  'llp',
  'inc',
  'nda',
  'faq',
  'usa',
  'us',
  'uk',
  'eu',
  'roi',
  'seo',
  'b2b',
  'b2c',
]);

const US_STATES = new Set(
  (
    'alabama alaska arizona arkansas california colorado connecticut delaware florida georgia hawaii idaho illinois indiana iowa kansas kentucky louisiana maine maryland massachusetts michigan minnesota mississippi missouri montana nebraska nevada ' +
    'ohio oklahoma oregon pennsylvania tennessee texas utah vermont virginia washington wisconsin wyoming'
  ).split(' '),
);

const NEGATORS = ['without', 'not', 'no', 'never'] as const;

function words(text: string): string[] {
  return text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
}

function singular(word: string): string {
  return word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word;
}

function excludedWords(input: HookSpecificityInput): Set<string> {
  const names = [
    input.brandName ?? '',
    ...input.productNames,
    ...input.offerNames,
    ...input.competitorNames,
  ];
  const out = new Set<string>();
  for (const name of names) for (const word of words(name)) out.add(singular(word));
  // An acronym spelled from an offering's initials ("Property Tax Optimization
  // (PTO)") is the offering by another name.
  for (const name of input.offerNames) {
    const initials = words(name.replace(/\(.*?\)/g, ''))
      .map((word) => word[0])
      .join('');
    if (initials.length >= 2) out.add(initials);
  }
  return out;
}

function evidenceStates(evidence: string, candidate: string): boolean {
  return evidence.includes(candidate);
}

const FUNCTION_WORDS = new Set(
  'a an the of and or to in on for with by at from that this these those is are was were be been it its as their your our we you they them than but not no without who which what when where how new also into over only more most such each any all can may will would could should has have had do does did'.split(
    ' ',
  ),
);

const SHARED_RUN_MIN_TOKENS = 3;
const SHARED_RUN_MIN_CONTENT_WORDS = 2;

function tokensOf(text: string): string[] {
  return words(text).map(singular);
}

/**
 * The longest run of consecutive words the hook shares with a cited evidence
 * line that is NOT support (a reassurance or a credential), counted only when
 * it holds enough words that are neither function words nor the brand's own
 * ("use of tax breaks", "ownership"). It is how a hook built on a source FACT
 * stated in the source's words passes without a figure, while a hook that
 * restates an FAQ answer or a staffing line does not.
 */
function sharedSourceWords(
  hook: string,
  evidence: readonly string[],
  excluded: ReadonlySet<string>,
): string | null {
  const hookTokens = tokensOf(hook);
  let best: string | null = null;
  let bestContent = 0;
  for (const line of evidence) {
    const kind = classifyEvidenceLine(line);
    if (kind === 'reassurance' || kind === 'credential') continue;
    const lineTokens = tokensOf(line);
    for (let i = 0; i < hookTokens.length; i += 1) {
      for (let j = 0; j < lineTokens.length; j += 1) {
        let k = 0;
        while (
          i + k < hookTokens.length &&
          j + k < lineTokens.length &&
          hookTokens[i + k] === lineTokens[j + k]
        ) {
          k += 1;
        }
        if (k < SHARED_RUN_MIN_TOKENS) continue;
        const run = hookTokens.slice(i, i + k);
        const content = run.filter(
          (token) => !FUNCTION_WORDS.has(token) && !excluded.has(token) && !/^\d+$/.test(token),
        );
        if (content.length >= SHARED_RUN_MIN_CONTENT_WORDS && content.length > bestContent) {
          bestContent = content.length;
          best = run.join(' ');
        }
      }
    }
  }
  return best;
}

export function hookSpecificity(input: HookSpecificityInput): HookSpecificity {
  const evidenceText = input.evidence.join('\n');
  const evidenceFigures = new Set(resultFigures(evidenceText));
  const hookFigures = resultFigures(input.hook).filter((figure) => evidenceFigures.has(figure));

  const excluded = excludedWords(input);
  const named = new Set<string>();

  const sequences = input.hook.matchAll(/\b[A-Z][\p{L}\p{N}-]*(?:\s+[A-Z][\p{L}\p{N}-]*)*/gu);
  for (const match of sequences) {
    const sequence = match[0];
    const tokens = sequence.split(/\s+/);
    const meaningful = tokens.filter((token) => {
      const key = singular(token.toLowerCase());
      return !excluded.has(key) && !CREDENTIAL_TERMS.has(key) && !CREDENTIAL_TERMS.has(key + 's');
    });
    if (meaningful.length === 0) continue;
    const phrase = meaningful.join(' ');
    if (!evidenceStates(evidenceText, phrase)) continue;

    if (meaningful.length >= 2) {
      named.add(phrase);
      continue;
    }
    const token = meaningful[0]!;
    const key = singular(token.toLowerCase());
    const isAcronym = /^[A-Z]{3,}s?$/.test(token);
    const atHookStart = match.index === 0;
    if (isAcronym) {
      named.add(token);
    } else if (!atHookStart && !US_STATES.has(key)) {
      named.add(token);
    }
  }

  const sourceWords = sharedSourceWords(input.hook, input.evidence, excluded);

  return {
    figures: hookFigures,
    named: [...named],
    sourceWords,
    specific: hookFigures.length > 0 || named.size > 0 || sourceWords !== null,
  };
}

/** A sentence a person reads as: this hook is about its own subject, not anyone's. */
export function describeSpecificity(result: HookSpecificity): string {
  if (result.figures.length > 0) {
    return `Carries ${result.figures.slice(0, 2).join(', ')}, a figure the cited evidence states.`;
  }
  if (result.named.length > 0) {
    return `Names ${result.named.slice(0, 2).join(', ')}, which the cited evidence states.`;
  }
  return `States a source fact in the source's own words ("${result.sourceWords}").`;
}

/**
 * Two negations stacked in one clause: "reviews without contacting assessors
 * without client authorization". It happens when a writer rebuilds a source
 * sentence that already carries its own condition ("We do not contact
 * assessors without your authorization.") and adds a second one on top.
 * The repeated word with no punctuation between is the countable form; the
 * source's own "not ... without" is a single condition and is left alone.
 */
export function findStackedNegation(text: string): string | null {
  for (const negator of NEGATORS) {
    const pattern = new RegExp(`\\b${negator}\\b([^.,;:!?—()]{0,60}?)\\b${negator}\\b`, 'i');
    const match = pattern.exec(text);
    if (match) return match[0];
  }
  return null;
}

const MECHANISM_CONNECTOR =
  /\b(?:through|via|by|using|thanks to|powered by|with|after|following)\s+([^,;.:!?—()]{3,80})/i;

const MIN_MECHANISM_WORD_CHARS = 4;

export interface UnstatedMechanism {
  /** The clause as written in the hook, connector included ("through ITC Tax valuation appeals"). */
  clause: string;
  /** The words of that clause no cited evidence line states. */
  unstated: string[];
}

/**
 * A figure in a hook is a result the page states, and a result tile states
 * only WHAT happened ("Data Centers. $5M. Tax Savings. Texas."). A writer then
 * adds HOW: "saved $5M in property tax through ITC Tax valuation appeals". The
 * page never said appeals produced that figure, so the hook has attached a
 * mechanism to a number it does not own (ITC run 0b33f38d, 2026-10-01).
 *
 * Rule: a clause joined to the figure by through/via/by/using/with/after must
 * be made of words the cited evidence states, apart from the brand, its
 * offerings and its competitors. Pure and deterministic; null when the hook
 * carries no evidenced figure or the clause is stated.
 */
export function findUnstatedMechanism(input: HookSpecificityInput): UnstatedMechanism | null {
  const evidenceText = input.evidence.join('\n');
  const evidenceFigures = new Set(resultFigures(evidenceText));
  const figureMatch = new RegExp(FIGURE_SOURCE, 'g');
  let after: string | null = null;
  for (const match of input.hook.matchAll(figureMatch)) {
    const canonical = resultFigures(match[0])[0];
    if (canonical && evidenceFigures.has(canonical)) {
      after = input.hook.slice(match.index + match[0].length);
      break;
    }
  }
  if (after === null) return null;

  const clause = MECHANISM_CONNECTOR.exec(after);
  if (!clause) return null;

  const excluded = excludedWords(input);
  const stated = new Set(tokensOf(evidenceText));
  const unstated = tokensOf(clause[1]!).filter(
    (token) =>
      token.length >= MIN_MECHANISM_WORD_CHARS &&
      !FUNCTION_WORDS.has(token) &&
      !excluded.has(token) &&
      !/^\d+$/.test(token) &&
      !stated.has(token),
  );
  if (unstated.length === 0) return null;
  return { clause: clause[0].trim(), unstated: [...new Set(unstated)] };
}
